import React, { useState } from "react";

function InfoTooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#8C8A96"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="cursor-default shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {visible && (
        <span className="absolute z-[9999] bottom-full left-0 mb-1.5 whitespace-nowrap rounded-lg border border-[#DFEAF2] bg-white px-2.5 py-1.5 text-[10px] font-medium text-[#525062] shadow-lg pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

type RankRow = {
  entity: string;
  avgDemand: number;
  peakDemand: number;
  avgTemp: number | null;
  avgHumidity: number | null;
  avgSolarIrradiance: number | null;
  netLoad: number;
  entityId?: number | null;
  peakDate?: string | null;
  peakSlot?: number | null;
  pctLoadCovered?: number | null;
};

type RankTableProps = {
  title: string;
  dotColor: string;
  tag?: string;
  rows: RankRow[];
  primaryWeather: string;
  onWeatherChange: (v: string) => void;
  onRowClick?: (row: RankRow) => void;
  loadUnit?: string;
  energyUnit?: string;
};

const PRIMARY_WEATHER_OPTIONS = [
  "Avg Temp",
  "Avg Humidity",
  "Avg Solar Irradiance",
];

function signed(val: number, decimals = 2) {
  return (val >= 0 ? "+" : "") + val.toFixed(decimals);
}

function colorClass(val: number) {
  return val < 0 ? "text-[#B23A2A]" : "text-[#1F7A4D]";
}

function formatNumber(val: number, maxDecimals = 2) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: maxDecimals,
  }).format(val);
}

const CELL = "text-[12px] font-medium";

function fmtDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function PeakBadge({
  date,
  slot,
}: {
  date?: string | null;
  slot?: number | null;
}) {
  if (!date && slot == null) return null;
  return (
    <span className="text-[8px] font-semibold text-[#34656D] bg-[#E8F4F8] pl-1.5 py-0.5 rounded-md flex items-center gap-1 shrink-0">
      {date && fmtDate(date)}
      {slot != null && (
        <span className="text-[8px] font-medium text-white bg-[#3D6B74] px-1 py-px rounded">
          {slot}
        </span>
      )}
    </span>
  );
}

const WEATHER_META: Record<
  string,
  { field: keyof RankRow; unit: string; decimals: number }
> = {
  "Avg Temp": { field: "avgTemp", unit: "°C", decimals: 2 },
  "Avg Humidity": { field: "avgHumidity", unit: "%", decimals: 2 },
  "Avg Solar Irradiance": {
    field: "avgSolarIrradiance",
    unit: "W/m²",
    decimals: 2,
  },
};

function weatherCell(r: RankRow, primaryWeather: string) {
  const meta = WEATHER_META[primaryWeather] ?? WEATHER_META["Temp"];
  const val = r[meta.field] as number | null;
  if (val == null) return <span className={`${CELL} text-[#9CA3AF]`}>—</span>;
  return (
    <span className={`${CELL} text-[#1F7A4D]`}>
      {val.toFixed(meta.decimals)}
      <span className="text-[10px] text-[#9CA3AF] ml-0.5">{meta.unit}</span>
    </span>
  );
}

type ColCtx = { primaryWeather: string; loadUnit: string; energyUnit: string };
const COLS: {
  label: string;
  key: keyof RankRow;
  render: (r: RankRow, ctx: ColCtx) => React.ReactNode;
}[] = [
  {
    label: "AVG DEMAND",
    key: "avgDemand",
    render: (r, { loadUnit }) => (
      <span className={`${CELL} text-[#1F7A4D]`}>
        {formatNumber(r.avgDemand)}
        <span className="text-[10px] text-[#9CA3AF] ml-0.5">{loadUnit}</span>
      </span>
    ),
  },
  {
    label: "PEAK DEMAND",
    key: "peakDemand",
    render: (r, { loadUnit }) => (
      <span className="inline-flex items-center gap-1.5 justify-start flex-wrap">
        <span className={`${CELL} text-[#1F7A4D]`}>
          {formatNumber(r.peakDemand)}
          <span className="text-[10px] text-[#9CA3AF] ml-0.5">{loadUnit}</span>
        </span>
        <PeakBadge date={r.peakDate} slot={r.peakSlot} />
      </span>
    ),
  },
  {
    label: "% OF LOAD",
    key: "pctLoadCovered",
    render: (r) => {
      const val = r.pctLoadCovered ?? null;
      if (val == null)
        return <span className={`${CELL} text-[#9CA3AF]`}>—</span>;
      return (
        <span className={`${CELL} text-[#1F7A4D]`}>
          {val.toFixed(2)}
          <span className="text-[10px] text-[#9CA3AF] ml-0.5">%</span>
        </span>
      );
    },
  },
  {
    label: "AVG TEMP",
    key: "avgTemp",
    render: (r, { primaryWeather }) => weatherCell(r, primaryWeather),
  },
];

export function RankTable({
  title,
  dotColor,
  tag,
  rows,
  primaryWeather,
  onWeatherChange,
  loadUnit = "MW",
  energyUnit = "MWh",
  onRowClick,
}: RankTableProps) {
  const [weatherOpen, setWeatherOpen] = React.useState(false);
  const weatherRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!weatherOpen) return;
    const handler = (e: MouseEvent) => {
      if (weatherRef.current && !weatherRef.current.contains(e.target as Node))
        setWeatherOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [weatherOpen]);

  const isGen = tag === "Generation";
  const cols = COLS.filter((c) => c.key !== "pctLoadCovered" || isGen).map(
    (c) => {
      if (c.key === "avgTemp") return { ...c, label: primaryWeather };
      if (c.key === "avgDemand")
        return { ...c, label: isGen ? "AVG GENERATION" : "AVG DEMAND" };
      if (c.key === "peakDemand")
        return { ...c, label: isGen ? "PEAK GENERATION" : "PEAK DEMAND" };
      return c;
    },
  );

  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-3">
      {/* header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: dotColor }}
          />
          <span className="text-[12px] font-medium text-[#14171F]">
            {title}
          </span>
          <InfoTooltip text="Based on Avg. Load" />
        </div>
        {tag && <span className="text-[10px] text-[#6B6760]">{tag}</span>}
      </div>

      {/* table */}
      <table id="metricTable" className="w-full text-left border-collapse">
        <thead>
          <tr>
            <th className="text-[10px] text-left font-medium text-[#6B6760] uppercase tracking-wide pb-2 pr-4">
              Entity
            </th>
            {cols.map((c) => (
              <th
                key={c.key}
                className="text-[10px] font-medium text-[#6B6760] uppercase tracking-wide pb-2 pr-4 text-left"
              >
                {c.key === "avgTemp" ? (
                  <span
                    ref={weatherRef}
                    className="relative inline-flex items-center gap-1 cursor-pointer select-none"
                    onClick={() => setWeatherOpen((o) => !o)}
                  >
                    <span className="text-[10px] font-medium text-[#6B6760] uppercase tracking-wide">
                      {primaryWeather}
                    </span>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      style={{
                        transform: weatherOpen ? "rotate(180deg)" : "none",
                        transition: "transform 0.15s",
                      }}
                    >
                      <path
                        d="M2 3.5L5 6.5L8 3.5"
                        stroke="#6B6760"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {weatherOpen && (
                      <span
                        className="absolute top-full right-0 mt-1 z-50 bg-white border border-[#DFEAF2] rounded-lg shadow-md overflow-hidden"
                        style={{ minWidth: 160 }}
                      >
                        {PRIMARY_WEATHER_OPTIONS.map((opt) => (
                          <span
                            key={opt}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              onWeatherChange(opt);
                              setWeatherOpen(false);
                            }}
                            className={`flex items-center gap-2 px-3 py-2 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
                              opt === primaryWeather
                                ? "bg-[#FDE7D9] text-[#A44E1E]"
                                : "text-[#525062] hover:bg-[#F5F7FA]"
                            }`}
                          >
                            <span className="w-3 shrink-0 flex items-center justify-center">
                              {opt === primaryWeather && (
                                <svg
                                  width="8"
                                  height="8"
                                  viewBox="0 0 8 8"
                                  fill="none"
                                >
                                  <path
                                    d="M1 4L3 6L7 2"
                                    stroke="#A44E1E"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                            {opt}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={cols.length + 1}
                className="py-6 text-center text-[11px] text-[#9CA3AF]"
              >
                No data available
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i} className="border-t border-[#F1F5FB]">
                <td className="py-2 pr-4 max-w-[160px]">
                  <div
                    className={`flex items-center gap-2 min-w-0 ${onRowClick && row.entityId != null ? "cursor-pointer group" : ""}`}
                    onClick={
                      onRowClick && row.entityId != null
                        ? () => onRowClick(row)
                        : undefined
                    }
                  >
                    <span
                      className="w-[3px] h-4 rounded-full shrink-0"
                      style={{ backgroundColor: dotColor }}
                    />
                    <span
                      className="text-[12px] font-medium text-[#14171F] truncate group-hover:underline"
                      title={row.entity}
                    >
                      {row.entity}
                    </span>
                    {onRowClick && row.entityId != null && (
                      <button
                        className="shrink-0 text-[#6B7280] group-hover:text-[#14171F] transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                        title={`Open ${row.entity}`}
                        tabIndex={-1}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M7 1h4v4M5 7l6-6M2 3h3M2 3v7h7V7"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className="py-2 pr-4 text-left font-medium text-[12px]"
                  >
                    {c.render(row, { primaryWeather, loadUnit, energyUnit })}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Feeder Summary ──────────────────────────────────────────────────────────

type FeederRow = {
  entity: string;
  entityId?: number | null;
  availablePct: number;
  zerosPct: number;
  nansPct: number;
};

type FeederTableProps = {
  title: string;
  dotColor: string;
  rows: FeederRow[];
  onRowClick?: (row: FeederRow) => void;
};

const FCELL = "text-[12px] font-medium";

const FEEDER_COLS: {
  label: string;
  key: keyof FeederRow;
  render: (r: FeederRow) => React.ReactNode;
}[] = [
  {
    label: "AVAILABLE %",
    key: "availablePct",
    render: (r) => (
      <span className={`${FCELL} text-[#1F7A4D]`}>
        {r.availablePct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] ml-0.5">%</span>
      </span>
    ),
  },
  {
    label: "% OF ZEROS",
    key: "zerosPct",
    render: (r) => (
      <span className={`${FCELL} text-[#1F7A4D]`}>
        {r.zerosPct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] ml-0.5">%</span>
      </span>
    ),
  },
  {
    label: "% OF Missing",
    key: "nansPct",
    render: (r) => (
      <span className={`${FCELL} text-[#1F7A4D]`}>
        {r.nansPct.toFixed(2)}
        <span className="text-[10px] text-[#9CA3AF] ml-0.5">%</span>
      </span>
    ),
  },
];

export function FeederTable({
  title,
  dotColor,
  rows,
  onRowClick,
}: FeederTableProps) {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: dotColor }}
        />
        <span className="text-[12px] font-medium text-[#14171F]">{title}</span>
      </div>
      <table id="feederTable" className="w-full text-left border-collapse">
        <thead>
          <tr>
            <th className="text-[10px] text-left font-medium text-[#6B6760] uppercase tracking-wide pb-2 pr-4">
              Entity
            </th>
            {FEEDER_COLS.map((c) => (
              <th
                key={c.key}
                className="text-[10px] font-medium text-[#6B6760] uppercase tracking-wide pb-2 pr-4 text-left"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={FEEDER_COLS.length + 1}
                className="py-6 text-center text-[11px] text-[#9CA3AF]"
              >
                No data available
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={i}
                className={`border-t border-[#F1F5FB] ${
                  onRowClick
                    ? "cursor-pointer hover:bg-[#F9FAFB] transition-colors group"
                    : ""
                }`}
                onClick={() => onRowClick?.(row)}
              >
                <td className="py-2 pr-4 max-w-[140px]">
                  <div
                    className={`flex items-center gap-2 min-w-0 ${onRowClick && row.entityId != null ? "group" : ""}`}
                  >
                    <span
                      className="w-[3px] h-4 rounded-full shrink-0"
                      style={{ backgroundColor: dotColor }}
                    />
                    <span
                      className={`text-[12px] font-medium text-[#14171F] truncate ${
                        onRowClick && row.entityId != null
                          ? "group-hover:underline"
                          : ""
                      }`}
                      title={row.entity}
                    >
                      {row.entity}
                    </span>
                    {onRowClick && row.entityId != null && (
                      <button
                        className="shrink-0 text-[#6B7280] group-hover:text-[#14171F] transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                        title={`Open ${row.entity}`}
                        tabIndex={-1}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M7 1h4v4M5 7l6-6M2 3h3M2 3v7h7V7"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
                {FEEDER_COLS.map((c) => (
                  <td
                    key={c.key}
                    className="py-2 pr-4 text-left font-medium text-[12px]"
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export const FEEDER_HIGH_DATA: FeederRow[] = [
  {
    entity: "Heerapura GT-1",
    availablePct: -652.5,
    zerosPct: 0.92,
    nansPct: 0.91,
  },
  {
    entity: "Ramgarh CCGT",
    availablePct: -481.2,
    zerosPct: 0.84,
    nansPct: 0.78,
  },
  {
    entity: "Chhabra Stage-II",
    availablePct: -395.6,
    zerosPct: 0.71,
    nansPct: 0.66,
  },
];

export const FEEDER_LOW_DATA: FeederRow[] = [
  {
    entity: "Heerapura GT-1",
    availablePct: -652.5,
    zerosPct: 0.92,
    nansPct: 0.91,
  },
  {
    entity: "Ramgarh CCGT",
    availablePct: -481.2,
    zerosPct: 0.84,
    nansPct: 0.78,
  },
  {
    entity: "Chhabra Stage-II",
    availablePct: -395.6,
    zerosPct: 0.71,
    nansPct: 0.66,
  },
];

// ── Demand/Gen rank tables ───────────────────────────────────────────────────

export const TOP_3_DATA: RankRow[] = [
  {
    entity: "Heerapura GT-1",
    avgDemand: -652.5,
    peakDemand: 0.92,
    avgTemp: 0.91,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Ramgarh CCGT",
    avgDemand: -481.2,
    peakDemand: 0.84,
    avgTemp: 0.78,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Chhabra Stage-II",
    avgDemand: -395.6,
    peakDemand: 0.71,
    avgTemp: 0.66,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
];

export const BOTTOM_3_DATA: RankRow[] = [
  {
    entity: "Giral Lignite-I",
    avgDemand: -652.5,
    peakDemand: 0.92,
    avgTemp: 0.91,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Kalisindh U-2",
    avgDemand: -481.2,
    peakDemand: 0.84,
    avgTemp: 0.78,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
  {
    entity: "Suratgarh STPS-3",
    avgDemand: -395.6,
    peakDemand: 0.71,
    avgTemp: 0.66,
    avgHumidity: null,
    avgSolarIrradiance: null,
    netLoad: 4182,
  },
];
